import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import dateFormat from 'dateformat';
import { isThisYear } from 'date-fns';
import LogInlineLink from '#component/general/log-inline-link';
import { warehouseEventType } from '#type';
import { getDeviceUrl, getDateTimeFormat, momentFormat } from '#helper';

const EventDescription = ({ log, account, isEventTab }) => {
  const {
    warehouse,
    appliance,
    applianceGroup,
    alert,
    device,
    event: { id },
    timestamp,
    data
  } = log;

  const {
    CREATE_WAREHOUSE,
    EDIT_WAREHOUSE,
    DELETE_WAREHOUSE,
    CREATE_APPLIANCE,
    EDIT_APPLIANCE,
    DELETE_APPLIANCE,
    STOP_APPLIANCE,
    ADD_APPLIANCE_GROUP,
    EDIT_APPLIANCE_GROUP,
    DELETE_APPLIANCE_GROUP,
    ADD_DEVICE,
    REMOVE_DEVICE,
    ADD_APPLIANCE_GROUP_ALERT,
    EDIT_APPLIANCE_GROUP_ALERT,
    REMOVE_APPLIANCE_GROUP_ALERT,
    TRIGGER_APPLIANCE_GROUP_ALERT,
    ADD_DEVICE_ALERT,
    EDIT_DEVICE_ALERT,
    REMOVE_DEVICE_ALERT,
    ADD_ALERT_CONFIRMATION,
    EDIT_ALERT_CONFIRMATION,
    REMOVE_ALERT_CONFIRMATION
  } = warehouseEventType;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  try {
    const applianceTitle = `${appliance?.label}`;
    const applianceLabel = isEventTab ? '' : (
      <LogInlineLink to={`/warehouse/appliance-overview/${appliance?.id}`} text={applianceTitle} isMobile={isMobile} />
    );
    const alertLabel = `"${alert?.label}"`;
    const warehouseLabel = <LogInlineLink to={`/warehouse/edit/${warehouse.id}`} text={`"${warehouse.label}"`} isMobile={isMobile} />;
    const deviceTitle = `${device?.title || device?.uid}`;
    const deviceUrl = getDeviceUrl(device?.id, timestamp);
    const deviceLabel = (
      <LogInlineLink to={{ pathname: deviceUrl }} text={deviceTitle} isMobile={isMobile} />
    );
    const applianceGroupLabel = applianceGroup?.label ? `"${applianceGroup.label}"` : ' ';
    const alertConfirmationLabel = `"${data?.value}"`;
    const alertConfirmationAlertLabel = `"${data?.alert?.label}"`;
    const alertConfirmationAlertTimestamp = data?.mqttMessageTimestamp ?
      momentFormat(data?.mqttMessageTimestamp, account, isThisYear(new Date(data?.mqttMessageTimestamp)) ? 'dateTimePartial' : 'dateTimeShort') : '';

    const format = getDateTimeFormat(account.measurementSystem, 'dateTimePartial');
    const messageTimestamp = log.messageTimestamp ? dateFormat(new Date(log.messageTimestamp), format) : '';

    switch (id) {
      case CREATE_WAREHOUSE:
        return (
          <>
            Site
            {' '}
            {warehouseLabel}
            {' '}
            was created
          </>
        );
      case EDIT_WAREHOUSE:
        return (
          <>
            Site
            {' '}
            {warehouseLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_WAREHOUSE:
        return (
          <>
            Site
            {' '}
            {warehouseLabel}
            {' '}
            was removed
          </>
        );
      case CREATE_APPLIANCE:
        return (
          <>
            Application
            {' '}
            {applianceLabel}
            {' '}
            was added to site
            {' '}
            {warehouseLabel}
          </>
        );
      case EDIT_APPLIANCE:
        return (
          <>
            Application
            {' '}
            {applianceLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_APPLIANCE:
        return (
          <>
            Application
            {' '}
            {applianceLabel}
            {' '}
            was removed from site
            {' '}
            {warehouseLabel}
          </>
        );
      case STOP_APPLIANCE:
        return (
          <>
            Application
            {' '}
            {applianceLabel}
            {' '}
            was stopped
          </>
        );
      case ADD_APPLIANCE_GROUP:
        return (
          <>
            Device group
            {' '}
            {applianceGroupLabel}
            {' '}
            was added to application
            {' '}
            {applianceLabel}
          </>
        );
      case EDIT_APPLIANCE_GROUP:
        return (
          <>
            Device group
            {' '}
            {applianceGroupLabel}
            {' '}
            was edited
          </>
        );
      case DELETE_APPLIANCE_GROUP:
        return (
          <>
            Device group
            {' '}
            {applianceGroupLabel}
            {' '}
            was removed from application
            {' '}
            {applianceLabel}
          </>
        );
      case ADD_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was added to application
            {' '}
            {applianceLabel}
            {applianceGroupLabel !== ' ' ? `, group ${applianceGroupLabel}` : ''}
          </>
        );
      case REMOVE_DEVICE:
        return (
          <>
            Device
            {' '}
            {deviceLabel}
            {' '}
            was removed from application
            {' '}
            {applianceLabel}
            {applianceGroupLabel !== ' ' ? `, group ${applianceGroupLabel}` : ''}
          </>
        );
      case ADD_APPLIANCE_GROUP_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was added to device group
            {' '}
            {applianceGroupLabel}
          </>
        );
      case EDIT_APPLIANCE_GROUP_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was edited in device group
            {' '}
            {applianceGroupLabel}
          </>
        );
      case REMOVE_APPLIANCE_GROUP_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was removed from device group
            {' '}
            {applianceGroupLabel}
          </>
        );
      case TRIGGER_APPLIANCE_GROUP_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was triggered in application
            {' '}
            {applianceLabel}
            {' '}
            by device
            {' '}
            {deviceLabel}
            {' '}
            on
            {' '}
            {messageTimestamp}
          </>
        );
      case ADD_DEVICE_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was added to device
            {' '}
            {deviceLabel}
          </>
        );
      case EDIT_DEVICE_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was edited in device
            {' '}
            {deviceLabel}
          </>
        );
      case REMOVE_DEVICE_ALERT:
        return (
          <>
            Alert
            {' '}
            {alertLabel}
            {' '}
            was removed from device
            {' '}
            {deviceLabel}
          </>
        );
      case ADD_ALERT_CONFIRMATION:
        return (
          <>
            Verification
            {' '}
            {alertConfirmationLabel}
            {' '}
            was added
            {' '}
            for alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in application
            {' '}
            {applianceLabel}
          </>
        );
      case EDIT_ALERT_CONFIRMATION:
        return (
          <>
            Verification
            {' '}
            {alertConfirmationLabel}
            {' '}
            was edited
            {' '}
            for alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in application
            {' '}
            {applianceLabel}
          </>
        );
      case REMOVE_ALERT_CONFIRMATION:
        return (
          <>
            Verification was withdrawn
            {' '}
            from alert
            {' '}
            {alertConfirmationAlertLabel}
            {' '}
            triggered on
            {' '}
            {alertConfirmationAlertTimestamp}
            {' '}
            in application
            {' '}
            {applianceLabel}
          </>
        );
      default:
        return 'Not recognized event';
    }
  } catch (e) {
    return 'Not recognized event';
  }
};

export default EventDescription;
